import { FunctionComponent, useMemo, useState } from 'react';
import moment, { Moment } from 'moment-timezone';
import styles from './HydrationComponent.module.scss';
import { Hydration } from 'model/NewMultipleBooking';
import { TimeSelector } from 'components/UI/V3/Forms/TimeSelector/TimeSelector';
import ExpandableSelector from 'components/UI/V3/ExpandableSelector';
import { DatePicker } from 'components/UI/V3/DatePicker/DatePicker';
import { useTimeZone } from 'hooks';

interface HydrationComponentProps {
    hydrationState: Hydration;
    onHydrationChange: (hydration: Hydration) => void;
    date: Moment;
    onDateChange: (value: Moment) => void;
}

type TimeOption = {
    id: string;
    label: string;
};

const findSelectedTimeOption = (time: string | undefined, timeOptions: Array<TimeOption>): TimeOption | undefined => {
    if (!time?.trim()) {
        return undefined;
    }

    const parsed = moment(time.trim(), ['h:mma', 'hh:mma', 'h:mm a', 'hh:mm a', 'h:mm A', 'hh:mm A'], true);

    if (!parsed.isValid()) {
        return undefined;
    }

    const timeId = parsed.format('hh:mm A');
    return timeOptions.find(option => option.id === timeId);
};

const HydrationComponent: FunctionComponent<HydrationComponentProps> = ({
    hydrationState,
    onHydrationChange,
    date,
    onDateChange
}) => {
    const timeZone = useTimeZone();
    const [isDateExpanded, setIsDateExpanded] = useState(false);

    const dateForPicker = useMemo(() => date.clone().tz(timeZone).startOf('day'), [date, timeZone]);

    const timeOptions: Array<TimeOption> = useMemo(() => {
        const options: TimeOption[] = [];
        const base = moment.tz(timeZone).startOf('day');

        for (let i = 0; i < 96; i++) {
            const time = base.clone().add(i * 15, 'minutes');
            options.push({
                id: time.format('hh:mm A'),
                label: time.format('h:mma')
            });
        }

        return options;
    }, [timeZone]);

    const selectedTimeOption = useMemo(
        () => findSelectedTimeOption(hydrationState.time, timeOptions),
        [hydrationState.time, timeOptions]
    );

    const handleDateChange = (newDate: Moment | null) => {
        if (!newDate) {
            return;
        }

        onDateChange(newDate.clone().tz(timeZone, true).startOf('day'));
        setIsDateExpanded(false);
    };

    const handleTimeChange = (timeId: string) => {
        const parsed = moment.tz(`${dateForPicker.format('YYYY-MM-DD')} ${timeId}`, 'YYYY-MM-DD hh:mm A', timeZone);
        const formattedTime = parsed.format('h:mma').toLowerCase();
        onHydrationChange({ ...hydrationState, time: formattedTime });
    };

    return (
        <>
            <div className={styles.dateTimeRow}>
                <ExpandableSelector
                    title="Date"
                    id="hydration-date-selector"
                    expanded={isDateExpanded}
                    label={dateForPicker.format('dddd, DD MMM YYYY')}
                    onExpand={() => setIsDateExpanded(prev => !prev)}
                    onClickAway={() => setIsDateExpanded(false)}
                    dropdownAlign="left"
                >
                    <DatePicker
                        timeZone={timeZone}
                        value={dateForPicker}
                        onChange={handleDateChange}
                    />
                </ExpandableSelector>

                <TimeSelector
                    label="Time"
                    value={selectedTimeOption}
                    options={timeOptions}
                    onChange={handleTimeChange}
                />
            </div>
            <div className={styles.noteSection}>
                <p className={styles.label}>Task Note</p>
                <textarea
                    className={styles.notesTextarea}
                    value={hydrationState.notes ?? ''}
                    onChange={e => onHydrationChange({ ...hydrationState, notes: e.target.value })}
                    placeholder="Notes here..."
                    rows={3}
                />
            </div>
        </>
    );
};

export default HydrationComponent;
